// Меню «⋯» у чата, папки или документа и поле для названия папки — общие для боковой панели и «Моих документов».
import { useEffect, useRef, useState } from "react";
import { Icon } from "./Icon";

// items — вложенное меню («Переместить в…»): пункт открывает его на месте, «Назад» возвращает.
// divider — черта над пунктом (перед «Выйти» или «Удалить»).
export type MenuAction = {
  label: string; icon: string; danger?: boolean; divider?: boolean; run?: () => void; items?: MenuAction[]; checked?: boolean;
};

const ROW = 36;

// Меню «⋯» у чата, папки или документа. Меню закреплено на экране (position: fixed), чтобы панель его не обрезала.
// children и className — своя кнопка вместо «⋯» (скрепка в чате, аккаунт); above — меню над кнопкой (аккаунт внизу панели).
export function ActionMenu({ label, actions, children, className = "saved-more", disabled, above = false, title }: {
  label: string; actions: MenuAction[]; children?: React.ReactNode; className?: string; disabled?: boolean; above?: boolean; title?: string;
}) {
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  const [sub, setSub] = useState<MenuAction | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!place) return;
    menu.current?.querySelector("button")?.focus();
  }, [place, sub]);
  useEffect(() => {
    if (!place) return;
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (event.type === "pointerdown" && (menu.current?.contains(event.target as Node) || button.current?.contains(event.target as Node))) return;
      setPlace(null);
      setSub(null);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("resize", close);
    document.getElementById("sidebar")?.addEventListener("scroll", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("resize", close);
      document.getElementById("sidebar")?.removeEventListener("scroll", close);
    };
  }, [place]);
  const heightOf = (list: MenuAction[], back = false) => 12 + (list.length + (back ? 1 : 0)) * ROW + list.filter((a) => a.divider).length * 9;
  const position = (list: MenuAction[], back = false) => {
    const rect = button.current!.getBoundingClientRect();
    const width = 280;
    const height = heightOf(list, back);
    if (above) return { top: Math.max(8, rect.top - height - 6), left: Math.max(8, Math.min(rect.right - width, innerWidth - width - 8)) };
    const top = Math.max(8, Math.min(rect.top, innerHeight - height - 8));
    return rect.right + 8 + width <= innerWidth ? { top, left: rect.right + 8 }
      : { top: Math.max(8, Math.min(rect.bottom + 4, innerHeight - height - 8)), left: Math.max(8, rect.right - width) };
  };
  const toggle = () => {
    if (place) { setPlace(null); setSub(null); return; }
    setPlace(position(actions));
  };
  const open = (action: MenuAction) => {
    if (action.items) {
      setSub(action);
      setPlace(position(action.items, true));
      return;
    }
    setPlace(null);
    setSub(null);
    action.run?.();
  };
  const list = sub?.items || actions;
  return (
    <>
      <button ref={button} type="button" className={`${className}${place ? " is-open" : ""}`} aria-label={label}
        title={title || (children ? label : "Ещё")} aria-haspopup="menu" aria-expanded={!!place} disabled={disabled} onClick={toggle}>
        {children || <Icon name="more" />}
      </button>
      {place && (
        <div ref={menu} className="chat-menu" role="menu" aria-label={sub ? sub.label : label} style={place}>
          {sub && (
            <button type="button" role="menuitem" className="menu-back" aria-label="Назад" title="Назад"
              onClick={() => { setSub(null); setPlace(position(actions)); }}>
              <Icon name="chevron" />{sub.label}
            </button>
          )}
          {list.map((action) => (
            <div key={action.label} className="menu-row">
              {action.divider && <hr className="menu-divider" />}
              <button type="button" role={action.checked === undefined ? "menuitem" : "menuitemradio"} aria-checked={action.checked}
                className={[action.danger && "danger", action.items && "has-items"].filter(Boolean).join(" ") || undefined}
                aria-haspopup={action.items ? "menu" : undefined} onClick={() => open(action)}>
                <Icon name={action.icon} />{action.label}{action.items && <span className="menu-more"><Icon name="chevron" /></span>}
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// Название папки или документа вводится прямо в списке: Enter — сохранить, Escape — отменить.
export function FolderNameField({ initial, label, onSave, onDone, placeholder = "Название папки", maxLength = 60 }: {
  initial: string; label: string; onSave: (name: string) => void; onDone: () => void; placeholder?: string; maxLength?: number;
}) {
  const [value, setValue] = useState(initial);
  const done = useRef(false);
  const save = () => {
    if (done.current) return;
    done.current = true;
    const name = value.trim();
    if (name && name !== initial) onSave(name);
    onDone();
  };
  return (
    <input className="saved-rename" value={value} autoFocus maxLength={maxLength} aria-label={label} placeholder={placeholder}
      onFocus={(event) => event.currentTarget.select()} onChange={(event) => setValue(event.target.value)} onBlur={save}
      onKeyDown={(event) => {
        if (event.key === "Enter") { event.preventDefault(); save(); }
        if (event.key === "Escape") { done.current = true; onDone(); }
      }} />
  );
}
